'use client';
import { useState } from "react";
import HeroImage from "@components/heroFaded";
import { track } from "@lib/track";
import { getUpcomingRetreats, type Retreat } from "@/app/data/retreats";
import { getFeeOptional } from "@lib/fees";

/** Per-person retreat deposit — env-sourced (NEXT_PUBLIC_RETREAT_DEPOSIT). */
const DEPOSIT_PP = getFeeOptional("retreatDeposit") ?? 0;

// Upcoming only — a past retreat must never be selectable on the deposit form.
const cfRetreats = getUpcomingRetreats().filter((r) => r.type === "cf");

function paymentDueFor(retreat: Retreat) {
  const d = new Date(retreat.startDate);
  d.setMonth(d.getMonth() - 1);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
}

const BookingForm = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [selectedRetreatIdx, setSelectedRetreatIdx] = useState<number | "">(cfRetreats.length === 1 ? 0 : "");
  const [roomType, setRoomType] = useState("");
  const [secondName, setSecondName] = useState("");
  const [secondEmail, setSecondEmail] = useState("");
  const [deposit, setDeposit] = useState(DEPOSIT_PP);
  const [price, setPrice] = useState(0);

  const selectedRetreat = selectedRetreatIdx !== "" ? cfRetreats[selectedRetreatIdx] : null;

  
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedRetreat) {
      alert("Please select a retreat date.");
      return;
    }

    track(
      "cta_click",
      { label: "Retreats | Book", component: "RetreatBookingPage" },
      { event: "cta_click", label: "Retreats | Book", component: "RetreatBookingPage", meta: { roomType, deposit } }
    );

    setSubmitting(true);
    try {
      const res = await fetch(`/api/retreats/initiate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          retreatId: selectedRetreat.id,
          roomType,
          name,
          email,
          secondName: roomType === "Shared (Couple)" ? secondName : undefined,
          secondEmail: roomType === "Shared (Couple)" ? secondEmail : undefined,
        }),
      });
      const data = await res.json();
      if (res.ok && data.paymentUrl) {
        window.location.href = data.paymentUrl;
      } else {
        console.error("Retreat booking failed:", data);
        alert(data.error || "Failed to create payment link. Please try again.");
        setSubmitting(false);
      }
    } catch (error) {
      console.error("Error creating payment link:", error);
      alert("An error occurred. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div>
    <HeroImage src="/images/laMangaCut.webp" alt="Naturopathic nutrition consultation at CF Organic Wellness" priority />
    <form
      onSubmit={handleSubmit}
      className="max-w-2xl mx-auto my-2 bg-white ring-1 ring-black/5 rounded-2xl p-6 space-y-6"
    >
      <h2 className="text-xl md:text-2xl font-semibold text-[var(--color-brand)]">
        Book Your Retreat
      </h2>
      <p className="text-[var(--color-brand)]">Complete your details below and make your deposit to reserve your space, full payment due 1 month prior to Retreat Commencing{selectedRetreat ? ` (${paymentDueFor(selectedRetreat)})` : ""}.</p>

      <div>
  <label className="block text-sm font-medium text-[var(--color-brand)]">Guest Name:</label>
  <input
    type="text"
    value={name}
    onChange={(e) => setName(e.target.value)}
    required
    className="mt-1 block w-full p-2 rounded-md border-gray-300 shadow-sm text-[var(--color-text-ink)] focus:border-[var(--color-brand)] focus:ring-[var(--color-brand)]"
  />
</div>

<div>
  <label className="block text-sm font-medium text-[var(--color-brand)]">Guest Email:</label>
  <input
    type="email"
    value={email}
    onChange={(e) => setEmail(e.target.value)}
    required
    className="mt-1 block w-full rounded-md p-2 border-gray-300 shadow-sm text-[var(--color-text-ink)] focus:border-[var(--color-brand)] focus:ring-[var(--color-brand)]"
  />
</div>

<div>
  <label className="block text-sm font-medium text-[var(--color-brand)]">Room Type:</label>
  <select
    value={roomType}
    onChange={(e) => {
      setRoomType(e.target.value);
      if (e.target.value === "Single (own)") {
        setPrice(2300);
        setDeposit(DEPOSIT_PP);
      } else if (e.target.value === "Shared (Couple)") {
        setPrice(2800);
        setDeposit(DEPOSIT_PP * 2); // two people sharing
      } else {
        setPrice(0);
        setDeposit(0);
      }
    }}
    required
    className="mt-1 block w-full rounded-md p-2 border-gray-300 shadow-sm text-[var(--color-text-ink)] focus:border-[var(--color-brand)] focus:ring-[var(--color-brand)]"
  >
    <option value="">Select...</option>
    <option value="Single (own)">Single (Own Room)</option>
    <option value="Shared (Couple)">Shared Room (Couple)</option>
  </select>
</div>

{/* Price and Deposit Confirmation */}
{roomType && (
  <div className="mt-4">
    <p className="text-sm text-[var(--color-text-secondary)] m-2">
      <strong>Price:</strong> £{price}
    </p>
    <p className="text-sm text-[var(--color-text-secondary)]">
      <strong>Deposit:</strong> £{deposit}
    </p>
  </div>
)}

{roomType === "Shared (Couple)" && (
  <>
    <div>
      <label className="block text-sm font-medium text-[var(--color-brand)]">
        Second Guest Name:
      </label>
      <input
        type="text"
        value={secondName}
        onChange={(e) => setSecondName(e.target.value)}
        required
        className="mt-1 block w-full rounded-md p-2 border-gray-300 shadow-sm text-[var(--color-text-ink)] focus:border-[var(--color-brand)] focus:ring-[var(--color-brand)]"
      />
    </div>

    <div>
      <label className="block text-sm font-medium text-[var(--color-brand)]">
        Second Guest Email:
      </label>
      <input
        type="email"
        value={secondEmail}
        onChange={(e) => setSecondEmail(e.target.value)}
        required
        className="mt-1 block w-full rounded-md border-gray-300 p-2 shadow-sm text-[var(--color-text-ink)] focus:border-[var(--color-brand)] focus:ring-[var(--color-brand)]"
      />
    </div>
  </>
)}

      <div>
        <label className="block text-sm font-medium text-[var(--color-brand)]">Date:</label>
        <select
          value={selectedRetreatIdx}
          onChange={(e) => setSelectedRetreatIdx(e.target.value === "" ? "" : Number(e.target.value))}
          required
          className="mt-1 block w-full rounded-md p-2 border-gray-300 shadow-sm text-[var(--color-text-ink)] focus:border-[var(--color-brand)] focus:ring-[var(--color-brand)]"
        >
          <option value="">Select a date...</option>
          {cfRetreats.map((r, i) => (
            <option key={r.startDate} value={i}>{r.displayDates}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-[var(--color-brand)]">
          Deposit (PP):
        </label>
        <input
          type="number"
          value={deposit}
          readOnly
          className="mt-1 block w-full rounded-md p-2 border-gray-300 shadow-sm bg-gray-100 text-[var(--color-text-ink)]"
        />
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="rounded-md bg-[var(--btn-primary-bg)] text-[var(--btn-primary-text)] py-2 px-4 font-medium hover:bg-[var(--btn-primary-hover)] transition disabled:opacity-60"
      >
        {submitting ? "Redirecting to payment…" : "Secure Your Spot"}
      </button>
    </form>
    </div>
  );
};

export default BookingForm;